import MapClickHandler from 'components/BaseMap/components/MapClickHandler';
import { useMemo } from 'react';
import MapToolsPanel from 'components/BaseMap/components/MapToolsPanel/MapToolsPanel';
import {
  authorizedAreaRings,
  draftSameCode,
  permittedAreaForDraft,
} from '../../../../utils/femaJurisdictions.utils';
import {
  createIpawsPermittedAreaCheck,
  getIpawsAreaContainment,
  IPAWS_AREA_TYPES,
} from '../../../../utils/ipawsArea.utils';
import useIpawsAreaEditor from '../../hooks/useIpawsAreaEditor';
import IpawsAreaMapLayers from './components/IpawsAreaMapLayers/IpawsAreaMapLayers';
import IpawsAreaToolsPanel from './components/IpawsAreaToolsPanel/IpawsAreaToolsPanel';
import MapPointerTracker from './components/MapPointerTracker/MapPointerTracker';

/** Combines the IPAWS map interactions, shape layers, and editing controls. */
const IpawsAreaMapContent = ({
  alertData,
  authorizedJurisdictions = [],
  currentMapCenter,
  severityColor,
  dispatchAreaAction,
}) => {
  const { state, actions } = useIpawsAreaEditor(alertData, dispatchAreaAction);
  // Every area the COG may alert, its ring parsed once; the draft's own code is the selected one.
  const selectedSameCode = draftSameCode(alertData);
  const authorizedAreas = useMemo(
    () => authorizedAreaRings(authorizedJurisdictions, selectedSameCode),
    [authorizedJurisdictions, selectedSameCode],
  );
  const {
    activeTool,
    areaShapes,
    displayedAreaShapes,
    displayedSelectedShape,
    placementPreviewShape,
    selectedShape,
    selectedShapeId,
    selectedShapeLabel,
    editMode,
    canUndo,
    canRedo,
    canInsertPolygonVertex,
  } = state;
  const {
    cancelActiveTool,
    clearShapes,
    commitPolygonVertexMove,
    commitSelectedShapeUpdate,
    continuePolygon,
    deleteSelectedShape,
    finishActiveTool,
    placeShape,
    previewPolygonVertexMove,
    previewSelectedShapeUpdate,
    selectShape,
    setPointerLocation,
    startAreaTool,
    updateSelectedShape,
    setEditMode,
    undoShapeEdit,
    redoShapeEdit,
    cancelShapeEdits,
    insertPolygonVertex,
    removeSelectedPolygonVertex,
  } = actions;
  const tracksPointer =
    activeTool === IPAWS_AREA_TYPES.CIRCLE || activeTool === IPAWS_AREA_TYPES.SQUARE;
  const isNwemSelected = alertData?.deliveryChannels?.includes('NWEM');

  // The area every shape must stay inside, prepared once for each selected SAME code. The check is
  // the same one the findings use, so the map marks and the findings always agree.
  const areaCheck = useMemo(
    () =>
      createIpawsPermittedAreaCheck(
        permittedAreaForDraft(
          { geocodes: selectedSameCode ? [{ valueName: 'SAME', value: selectedSameCode }] : [] },
          authorizedJurisdictions,
        ),
      ),
    [authorizedJurisdictions, selectedSameCode],
  );
  // The displayed shapes include a drag in progress, so a mark follows the pointer.
  const containment = useMemo(
    () => getIpawsAreaContainment(displayedAreaShapes, areaCheck),
    [areaCheck, displayedAreaShapes],
  );
  const isPlacementPreviewOutside = useMemo(
    () =>
      Boolean(placementPreviewShape) &&
      getIpawsAreaContainment([placementPreviewShape], areaCheck).outsideShapeIds.length > 0,
    [areaCheck, placementPreviewShape],
  );

  return (
    <>
      <MapClickHandler onClick={(event) => placeShape(event.latlng)} />

      {/* Polygon points need only clicks. Circle and square outlines also follow the pointer. */}
      {tracksPointer && <MapPointerTracker onPointerChange={setPointerLocation} />}

      <IpawsAreaMapLayers
        activeTool={activeTool}
        displayedAreaShapes={displayedAreaShapes}
        displayedSelectedShape={displayedSelectedShape}
        authorizedAreas={authorizedAreas}
        onCommitPolygonVertexMove={commitPolygonVertexMove}
        onCommitSelectedShapeUpdate={commitSelectedShapeUpdate}
        onPreviewPolygonVertexMove={previewPolygonVertexMove}
        onPreviewSelectedShapeUpdate={previewSelectedShapeUpdate}
        onSelectShape={selectShape}
        placementPreviewShape={placementPreviewShape}
        selectedShape={selectedShape}
        selectedShapeId={selectedShapeId}
        selectedShapeLabel={selectedShapeLabel}
        severityColor={severityColor}
        editMode={editMode}
        canInsertVertex={canInsertPolygonVertex}
        onInsertVertex={insertPolygonVertex}
        onRemoveVertex={removeSelectedPolygonVertex}
        outsideShapeIds={containment.outsideShapeIds}
        outsidePoints={containment.outsidePoints}
        isPlacementPreviewOutside={isPlacementPreviewOutside}
      />

      <MapToolsPanel position="topright" width={280} header="IPAWS Alerting Tools" collapsible>
        <IpawsAreaToolsPanel
          shapes={areaShapes}
          activeTool={activeTool}
          selectedShapeId={selectedShapeId}
          outsideShapeIds={containment.outsideShapeIds}
          isNwemSelected={isNwemSelected}
          onStartTool={startAreaTool}
          onFinishTool={finishActiveTool}
          onCancelTool={cancelActiveTool}
          onPlaceAtMapCenter={() => placeShape(currentMapCenter)}
          onSelectShape={selectShape}
          onContinuePolygon={continuePolygon}
          onUpdateSelectedShape={updateSelectedShape}
          onDeleteSelectedShape={deleteSelectedShape}
          onClearShapes={clearShapes}
          editMode={editMode}
          canUndo={canUndo}
          canRedo={canRedo}
          onSetEditMode={setEditMode}
          onUndoEdit={undoShapeEdit}
          onRedoEdit={redoShapeEdit}
          onCancelEdits={cancelShapeEdits}
        />
      </MapToolsPanel>
    </>
  );
};

export default IpawsAreaMapContent;
